import { PDFDocument, type PDFFont, type PDFPage, rgb, StandardFonts } from "pdf-lib"

import { escribible, lineas } from "@/componentes/reportes/pdfReporte"
import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha } from "@/lib/fechas"

import type { Celda, Columna, DocumentoExportable, Indicador, Seccion } from "./modelo"

/**
 * El PDF de cualquier tabla o reporte, con la identidad de la agencia (29/10).
 *
 * ⚠ **Compuesto en el navegador con `pdf-lib`, como los reportes de Telegram**
 * (`componentes/reportes/pdfReporte.ts`, de donde salen `escribible` y `lineas`): la
 * impresión del navegador no funciona en buena parte de los teléfonos y sale con lo
 * que tenga la pantalla —el cristal, el menú—. Aquí sale siempre la misma hoja.
 *
 * La hoja es la de lo que la agencia imprime (`--impreso-*`): la marca vectorial, la
 * razón social con el descriptor y el RIF, la dirección y los contactos de «Empresa»,
 * el filete dorado y la raya vino; la cabecera de cada tabla en vino con letra blanca,
 * y al pie de CADA página la agencia, el título y «Página n de N».
 *
 * ⚠ **Carta, y apaisada cuando la tabla no cabe de pie**: se miden las columnas antes
 * de elegir. Las cifras nunca se parten; lo que se envuelve es el texto.
 *
 * ⚠ **La tabla sigue en la página siguiente repitiendo su cabecera**, y un rótulo de
 * grupo no se queda solo al pie.
 */

export type Membrete = {
  nombre: string
  rif: string | null
  direccion: string | null
  contacto: string | null
}

const TINTA = rgb(0.141, 0.063, 0.098) // --impreso-tinta #241019
const GRIS = rgb(0.42, 0.333, 0.376) // --impreso-gris #6b5560
const LINEA = rgb(0.89, 0.863, 0.875) // --impreso-linea #e3dcdf
const VINO = rgb(0.478, 0.106, 0.22) // --impreso-vino #7a1b38
const ORO = rgb(0.788, 0.604, 0.263) // --impreso-oro-2 #c99a43
const CEBRA = rgb(0.98, 0.965, 0.97)
const GRUPO = rgb(0.953, 0.918, 0.929)
const BLANCO = rgb(1, 1, 1)

const MARGEN = 36
const CUERPO = 7.8
const INTERLINEA = 9.6
const RELLENO = 4
const DESCRIPTOR = "AGENTES ADUANALES"

type Hoja = { ancho: number; alto: number }
const DE_PIE: Hoja = { ancho: 612, alto: 792 }
const APAISADA: Hoja = { ancho: 792, alto: 612 }

type Lienzo = {
  pdf: PDFDocument
  pagina: PDFPage
  hoja: Hoja
  y: number
  normal: PDFFont
  negrita: PDFFont
}

const cifra = (c: Columna) => c.tipo === "monto" || c.tipo === "numero"

/** Cómo se lee una celda en el papel: la cifra con sus separadores, la fecha en dd/mm/aaaa. */
export function comoTexto(c: Celda, columna: Columna): string {
  if (c === null || c === "") return columna.tipo === "texto" ? "" : "—"
  if (typeof c === "number") {
    if (columna.tipo === "numero")
      // Una cantidad o una tasa conserva sus decimales (hasta seis); un monto, dos.
      return c.toLocaleString("es-VE", { maximumFractionDigits: 6 })
    return monto(c.toFixed(2))
  }
  if (columna.tipo === "fecha") return comoFecha(c) || c
  return c
}

/** Lo que pide cada columna para no envolver, con su relleno. */
function naturalesDe(s: Seccion, fuente: PDFFont, negrita: PDFFont): number[] {
  return s.columnas.map((col, i) => {
    const titulo = negrita.widthOfTextAtSize(escribible(negrita, col.titulo.toUpperCase()), 6.8)
    const medidas = s.filas
      .filter((f) => !f.grupo)
      .map((f) =>
        fuente.widthOfTextAtSize(escribible(fuente, comoTexto(f.celdas[i] ?? null, col)), CUERPO),
      )
      .sort((a, b) => a - b)
    // El percentil 90: una celda larguísima no se lleva el ancho de toda la columna.
    const p90 = medidas.length ? (medidas[Math.floor(medidas.length * 0.9)] ?? 0) : 0
    const tope = cifra(col) || col.tipo === "fecha" ? 140 : 240
    return Math.min(Math.max(titulo, p90, 24) + 2 * RELLENO, tope + 2 * RELLENO)
  })
}

/** Anchos de columna que caben en `util`: las cifras a su medida; el texto, a lo que quede. */
export function anchos(s: Seccion, fuente: PDFFont, negrita: PDFFont, util: number): number[] {
  const naturales = naturalesDe(s, fuente, negrita)
  const total = naturales.reduce((a, b) => a + b, 0)
  if (total <= util) {
    // Lo que sobra se reparte entre las columnas de texto, que son las que envuelven.
    const texto = s.columnas.map((c) => c.tipo === "texto")
    const base = naturales.reduce((a, n, i) => a + (texto[i] ? n : 0), 0)
    if (base === 0) return naturales.map((n) => (n * util) / total)
    return naturales.map((n, i) => (texto[i] ? n + ((util - total) * n) / base : n))
  }
  const fijas = naturales.reduce((a, n, i) => a + (s.columnas[i]?.tipo === "texto" ? 0 : n), 0)
  const variables = total - fijas
  if (fijas >= util || variables === 0) return naturales.map((n) => (n * util) / total)
  const factor = (util - fijas) / variables
  return naturales.map((n, i) => (s.columnas[i]?.tipo === "texto" ? n * factor : n))
}

function escribir(
  l: Lienzo,
  t: string,
  x: number,
  y: number,
  o: {
    cuerpo?: number
    fuente?: PDFFont
    color?: ReturnType<typeof rgb>
    ancho?: number
    derecha?: boolean
  } = {},
) {
  const fuente = o.fuente ?? l.normal
  const cuerpo = o.cuerpo ?? CUERPO
  const limpio = escribible(fuente, t)
  const medida = fuente.widthOfTextAtSize(limpio, cuerpo)
  const px = o.derecha && o.ancho !== undefined ? x + o.ancho - medida : x
  l.pagina.drawText(limpio, { x: px, y, size: cuerpo, font: fuente, color: o.color ?? TINTA })
}

function nuevaPagina(l: Lienzo) {
  l.pagina = l.pdf.addPage([l.hoja.ancho, l.hoja.alto])
  l.y = l.hoja.alto - MARGEN
}

async function dibujarMembrete(l: Lienzo, m: Membrete, logo: Uint8Array | null) {
  const util = l.hoja.ancho - 2 * MARGEN
  const alto = 40
  if (logo) {
    const imagen = await l.pdf.embedPng(logo)
    const ancho = (imagen.width / imagen.height) * alto
    l.pagina.drawImage(imagen, { x: MARGEN, y: l.y - alto, width: ancho, height: alto })
  }
  escribir(l, m.nombre.toUpperCase(), MARGEN, l.y - 11, {
    cuerpo: 11.5,
    fuente: l.negrita,
    ancho: util,
    derecha: true,
  })
  // Sin repetirlo si la razón social ya lo dice («SETIECA AGENTES ADUANALES C.A.»).
  const conDescriptor = !m.nombre.toUpperCase().includes(DESCRIPTOR)
  if (conDescriptor)
    escribir(l, DESCRIPTOR, MARGEN, l.y - 22, {
      cuerpo: 7,
      color: VINO,
      ancho: util,
      derecha: true,
    })
  if (m.rif)
    escribir(l, `RIF ${m.rif}`, MARGEN, l.y - (conDescriptor ? 32 : 23), {
      cuerpo: 7.5,
      color: GRIS,
      ancho: util,
      derecha: true,
    })
  l.y -= alto + 6
  const detalle = [m.direccion, m.contacto].filter(Boolean).join("   ·   ")
  if (detalle) {
    for (const ln of lineas(l.normal, 6.8, detalle, util)) {
      escribir(l, ln, MARGEN, l.y, { cuerpo: 6.8, color: GRIS, ancho: util, derecha: true })
      l.y -= 8.5
    }
  }
  // El filete dorado de la firma corporativa, con la raya vino debajo.
  l.pagina.drawLine({
    start: { x: MARGEN, y: l.y },
    end: { x: l.hoja.ancho - MARGEN, y: l.y },
    thickness: 1.6,
    color: ORO,
  })
  l.pagina.drawLine({
    start: { x: MARGEN, y: l.y - 2.6 },
    end: { x: l.hoja.ancho - MARGEN, y: l.y - 2.6 },
    thickness: 0.6,
    color: VINO,
  })
  l.y -= 22
}

function cabecera(l: Lienzo, s: Seccion, w: number[]) {
  const alto = 15
  l.pagina.drawRectangle({
    x: MARGEN,
    y: l.y - alto + 4,
    width: w.reduce((a, b) => a + b, 0),
    height: alto,
    color: VINO,
  })
  let x = MARGEN
  s.columnas.forEach((col, i) => {
    const ancho = (w[i] ?? 0) - 2 * RELLENO
    const titulo = lineas(l.negrita, 6.8, col.titulo.toUpperCase(), ancho)[0] ?? ""
    escribir(l, titulo, x + RELLENO, l.y - 6, {
      cuerpo: 6.8,
      fuente: l.negrita,
      color: BLANCO,
      ancho,
      derecha: cifra(col),
    })
    x += w[i] ?? 0
  })
  l.y -= alto + 2
}

function fila(
  l: Lienzo,
  s: Seccion,
  w: number[],
  celdas: Celda[],
  o: { grupo?: boolean; total?: boolean; cebra?: boolean; repetir: () => void },
) {
  const anchoTotal = w.reduce((a, b) => a + b, 0)
  const fuente = o.grupo || o.total ? l.negrita : l.normal
  const contenido = o.grupo
    ? [lineas(fuente, CUERPO, String(celdas[0] ?? ""), anchoTotal - 2 * RELLENO)]
    : s.columnas.map((col, i) => {
        const t = comoTexto(celdas[i] ?? null, col)
        return cifra(col)
          ? [escribible(fuente, t)]
          : lineas(fuente, CUERPO, t, (w[i] ?? 0) - 2 * RELLENO)
      })
  const alto = Math.max(...contenido.map((c) => c.length)) * INTERLINEA + 2 * RELLENO - 2
  if (l.y - alto < MARGEN + 18) {
    nuevaPagina(l)
    o.repetir()
  }
  const fondo = o.grupo ? GRUPO : o.cebra ? CEBRA : null
  if (fondo)
    l.pagina.drawRectangle({
      x: MARGEN,
      y: l.y - alto + 6,
      width: anchoTotal,
      height: alto,
      color: fondo,
    })
  if (o.total)
    l.pagina.drawLine({
      start: { x: MARGEN, y: l.y + 6 },
      end: { x: MARGEN + anchoTotal, y: l.y + 6 },
      thickness: 0.9,
      color: VINO,
    })
  let x = MARGEN
  contenido.forEach((renglones, i) => {
    const col = s.columnas[i]
    const ancho = o.grupo ? anchoTotal - 2 * RELLENO : (w[i] ?? 0) - 2 * RELLENO
    renglones.forEach((ln, k) => {
      escribir(l, ln, x + RELLENO, l.y - 2 - k * INTERLINEA, {
        fuente,
        color: o.grupo ? VINO : TINTA,
        ancho,
        derecha: !o.grupo && col !== undefined && cifra(col),
      })
    })
    x += w[i] ?? 0
  })
  l.y -= alto
  l.pagina.drawLine({
    start: { x: MARGEN, y: l.y + 6 },
    end: { x: MARGEN + anchoTotal, y: l.y + 6 },
    thickness: 0.35,
    color: LINEA,
  })
}

function seccion(l: Lienzo, s: Seccion, conTitulo: boolean) {
  const util = l.hoja.ancho - 2 * MARGEN
  const w = anchos(s, l.normal, l.negrita, util)
  if (l.y < MARGEN + 80) nuevaPagina(l)
  const titular = (sigue: boolean) => {
    if (conTitulo) {
      escribir(l, `${s.titulo}${sigue ? " (continúa)" : ""}`, MARGEN, l.y, {
        cuerpo: 10,
        fuente: l.negrita,
        color: VINO,
      })
      l.y -= 14
    }
    cabecera(l, s, w)
  }
  titular(false)
  if (s.filas.length === 0) {
    escribir(l, "Sin registros con lo elegido.", MARGEN + RELLENO, l.y - 2, { color: GRIS })
    l.y -= 16
  }
  s.filas.forEach((f, i) => {
    // El total no se queda solo en otra página: se lleva consigo la última fila.
    if (i === s.filas.length - 1 && s.pie?.length && l.y - 46 < MARGEN + 18) {
      nuevaPagina(l)
      titular(true)
    }
    fila(l, s, w, f.celdas, {
      grupo: f.grupo,
      total: f.total,
      cebra: i % 2 === 1 && !s.cuadro,
      repetir: () => titular(true),
    })
  })
  for (const p of s.pie ?? []) fila(l, s, w, p, { total: true, repetir: () => titular(true) })
  if (!s.cuadro) {
    escribir(l, `${s.filas.filter((f) => !f.grupo).length} registros`, MARGEN, l.y - 4, {
      cuerpo: 6.5,
      color: GRIS,
    })
  }
  l.y -= s.cuadro ? 14 : 26
}

/** La hoja que hace falta: apaisada si alguna tabla no cabe de pie. */
function elegirHoja(d: DocumentoExportable, normal: PDFFont, negrita: PDFFont): Hoja {
  const util = DE_PIE.ancho - 2 * MARGEN
  const apretada = d.secciones.some((s) => {
    if (s.columnas.length > 7) return true
    return naturalesDe(s, normal, negrita).reduce((a, b) => a + b, 0) > util * 1.15
  })
  return apretada ? APAISADA : DE_PIE
}

/**
 * Los indicadores como TARJETAS, como en la pantalla: título, la cifra grande, los montos
 * por moneda y el detalle, con el filo vino a la izquierda. Tres por fila de pie, cuatro
 * apaisada.
 */
function tarjetas(l: Lienzo, lista: Indicador[]) {
  if (lista.length === 0) return
  const util = l.hoja.ancho - 2 * MARGEN
  const porFila = l.hoja.ancho > l.hoja.alto ? 4 : 3
  const hueco = 8
  const ancho = (util - hueco * (porFila - 1)) / porFila
  const interior = ancho - 16
  escribir(l, "INDICADORES", MARGEN, l.y, { cuerpo: 7.5, fuente: l.negrita, color: VINO })
  l.y -= 10
  for (let i = 0; i < lista.length; i += porFila) {
    const fila = lista.slice(i, i + porFila).map((k) => ({
      k,
      titulo: lineas(l.negrita, 7.8, k.titulo, interior).slice(0, 2),
      detalle: k.detalle ? lineas(l.normal, 6.3, k.detalle, interior).slice(0, 2) : [],
      montos: k.montos ?? [],
    }))
    const alto = Math.max(
      ...fila.map(
        (t) =>
          14 +
          t.titulo.length * 9.5 +
          t.detalle.length * 7.8 +
          (t.k.cantidad ? 17 : 0) +
          t.montos.length * 10,
      ),
    )
    if (l.y - alto < MARGEN + 18) nuevaPagina(l)
    fila.forEach((t, j) => {
      const x = MARGEN + j * (ancho + hueco)
      const arriba = l.y
      l.pagina.drawRectangle({
        x,
        y: arriba - alto,
        width: ancho,
        height: alto,
        color: CEBRA,
        borderColor: LINEA,
        borderWidth: 0.5,
      })
      l.pagina.drawRectangle({ x, y: arriba - alto, width: 2.5, height: alto, color: VINO })
      let y = arriba - 11
      for (const ln of t.titulo) {
        escribir(l, ln, x + 9, y, { cuerpo: 7.8, fuente: l.negrita })
        y -= 9.5
      }
      for (const ln of t.detalle) {
        escribir(l, ln, x + 9, y, { cuerpo: 6.3, color: GRIS })
        y -= 7.8
      }
      if (t.k.cantidad) {
        y -= 6
        escribir(l, t.k.cantidad, x + 9, y, { cuerpo: 13, fuente: l.negrita, color: VINO })
        y -= 11
      }
      for (const m of t.montos) {
        y -= 1
        escribir(l, m.moneda, x + 9, y, { cuerpo: 7.8, color: GRIS })
        escribir(l, monto(m.valor.toFixed(2)), x + 9, y, {
          cuerpo: 7.8,
          fuente: l.negrita,
          ancho: interior,
          derecha: true,
        })
        y -= 9
      }
    })
    l.y -= alto + hueco
  }
  l.y -= 12
}

export async function pdfExportable(
  d: DocumentoExportable,
  m: Membrete,
  o: { logo: Uint8Array | null; autor?: string; generado?: Date },
): Promise<Uint8Array<ArrayBuffer>> {
  const pdf = await PDFDocument.create()
  pdf.setTitle(d.titulo)
  pdf.setAuthor(m.nombre)
  pdf.setCreator("Sistema Aduanal 3.0")
  const normal = await pdf.embedFont(StandardFonts.Helvetica)
  const negrita = await pdf.embedFont(StandardFonts.HelveticaBold)
  const hoja = elegirHoja(d, normal, negrita)
  const l: Lienzo = {
    pdf,
    pagina: pdf.addPage([hoja.ancho, hoja.alto]),
    hoja,
    y: hoja.alto - MARGEN,
    normal,
    negrita,
  }
  const util = hoja.ancho - 2 * MARGEN

  await dibujarMembrete(l, m, o.logo)
  for (const ln of lineas(negrita, 15, d.titulo, util)) {
    escribir(l, ln, MARGEN, l.y, { cuerpo: 15, fuente: negrita })
    l.y -= 17
  }
  if (d.subtitulo) {
    for (const ln of lineas(normal, 8.5, d.subtitulo, util)) {
      escribir(l, ln, MARGEN, l.y, { cuerpo: 8.5, color: GRIS })
      l.y -= 11
    }
  }
  const g = o.generado ?? new Date()
  const dos = (n: number) => String(n).padStart(2, "0")
  const cuando = `${dos(g.getDate())}/${dos(g.getMonth() + 1)}/${g.getFullYear()} ${dos(g.getHours())}:${dos(g.getMinutes())}`
  escribir(l, `Generado el ${cuando}${o.autor ? ` por ${o.autor}` : ""}`, MARGEN, l.y, {
    cuerpo: 7.5,
    color: GRIS,
  })
  l.y -= 20

  tarjetas(l, d.indicadores ?? [])
  if (d.secciones.length === 0 && (d.indicadores ?? []).length === 0)
    escribir(l, "No hay tablas que exportar.", MARGEN, l.y, { color: GRIS })
  for (const s of d.secciones) seccion(l, s, d.secciones.length > 1 || s.titulo !== d.titulo)

  const paginas = pdf.getPages()
  paginas.forEach((p, i) => {
    const izq = escribible(normal, `${m.nombre} · ${d.titulo}`)
    const der = `Página ${i + 1} de ${paginas.length}`
    p.drawLine({
      start: { x: MARGEN, y: MARGEN - 8 },
      end: { x: hoja.ancho - MARGEN, y: MARGEN - 8 },
      thickness: 0.6,
      color: ORO,
    })
    p.drawText(izq.length > 110 ? `${izq.slice(0, 107)}…` : izq, {
      x: MARGEN,
      y: MARGEN - 19,
      size: 6.5,
      font: normal,
      color: GRIS,
    })
    p.drawText(der, {
      x: hoja.ancho - MARGEN - normal.widthOfTextAtSize(der, 6.5),
      y: MARGEN - 19,
      size: 6.5,
      font: normal,
      color: GRIS,
    })
  })
  return new Uint8Array(await pdf.save())
}
